import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TileButton } from '@cbhq/cds-web/buttons';
import { useTheme } from '@cbhq/cds-web/hooks/useTheme';
import { Box } from '@cbhq/cds-web/layout';
import { ThemeProvider } from '@cbhq/cds-web/system';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { coinbaseTheme } from '@cbhq/cds-web/themes/coinbaseTheme';
import { ActionType, AnalyticsEventImportance, ComponentType } from '@cbhq/client-analytics';
import { Fingerprint } from '@cbhq/identity-fingerprint';
import { isAppSwitch, WrappedPromise } from '@cbhq/identity-shared/utils';

import { useAnalytics } from '../hooks/useAnalytics';
import { useApplicationSourceImage } from '../hooks/useApplicationSourceImage';
import { useCustomAction } from '../hooks/useCustomAction';
import { Application } from '../providers/Applications.types';
import { useClientProps } from '../providers/ClientPropsProvider';

import { RemoteImage } from './RemoteImage';

function getApplicationUrl({
  applicationId,
  destinationUri,
  locale,
}: {
  applicationId: string;
  destinationUri: string;
  locale: string;
}) {
  let baseUrl = new URL(`${destinationUri}`);
  if (applicationId === 'HELP_CENTER') {
    baseUrl = new URL(`${destinationUri}/${locale}`);
  }

  baseUrl.searchParams.set('appswitcher', 'true');

  return baseUrl;
}

type Props = {
  application: Application;
  fingerprint: WrappedPromise<Fingerprint | undefined>;
};

export function ApplicationButton({ application, fingerprint }: Props) {
  const {
    applicationId,
    destinationUri,
    label,
    tag,
    id: apiProjectId,
    accessibilityLabel,
    transferSession,
  } = application;
  const { activeColorScheme: spectrum } = useTheme();
  const { projectId } = useClientProps();

  const { logEvent } = useAnalytics();
  const { locale } = useIntl();
  const customAction = useCustomAction(applicationId);

  const onClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      logEvent(
        'app_switcher_application_clicked',
        {
          action: ActionType.click,
          componentType: ComponentType.button,
          option_id: 'product',
          option_type: applicationId ?? 'error',
          url: destinationUri,
        },
        AnalyticsEventImportance.high,
      );
      if (customAction) {
        customAction();
        return;
      }

      const url = getApplicationUrl({ applicationId, destinationUri, locale });

      if (transferSession) {
        const fp = await fingerprint.promise;
        url.searchParams.set('requestId', fp?.requestId ?? '');
        url.searchParams.set('sealedResult', fp?.sealedResult ?? '');
      }

      if (isAppSwitch({ url, apiProjectId, projectId })) {
        window.open(url, '_blank')?.focus();
        return;
      }

      window.location.href = url.href;
    },
    [
      logEvent,
      applicationId,
      destinationUri,
      customAction,
      locale,
      apiProjectId,
      projectId,
      transferSession,
      fingerprint,
    ],
  );

  const title = useMemo(() => {
    return label;
  }, [label]);

  const sourceImage = useApplicationSourceImage(application);

  return (
    <ThemeProvider theme={coinbaseTheme} activeColorScheme={spectrum} isolated>
      <Box position="relative" testID="ApplicationButton">
        <TileButton
          testID="ApplicationButton-TileButton"
          accessibilityLabel={accessibilityLabel}
          onClick={onClick}
          title={title}
          height="100%"
          block
        >
          <RemoteImage source={sourceImage ?? ''} />
        </TileButton>
        {tag && (
          <Tag
            intent={tag.intent}
            colorScheme={tag.colorScheme}
            position="absolute"
            top={5}
            right={0}
          >
            {tag.label}
          </Tag>
        )}
      </Box>
    </ThemeProvider>
  );
}
